import React, {forwardRef, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState} from 'react'

import {Input} from './index.js'
import {InputChangeEvent, InputRef} from './Input.js'
import {FieldProps, FieldRef, InvalidProps} from './types.js'

export interface MetricInputProps extends FieldProps, InvalidProps {
    id?: string
    // In the stored unit, the input shows value / divisionFactor (e.g. grams shown as kg with divisionFactor 1000)
    value?: number
    divisionFactor?: number
    // Only called on blur and Enter when the text was changed, with the value in the stored unit (0 for an empty field)
    onChange?: (event: {target: {value: number}}) => void
    // Extra decimals allowed on top of the ones divisionFactor gives
    precision?: number
    // In the stored unit
    min?: number
    max?: number
    // A number is in the stored unit and is shown divided by divisionFactor
    placeholder?: string | number
    prepend?: React.ReactNode
    append?: React.ReactNode
}

export type MetricInputRef = FieldRef

const toDisplayValue = (value: number | undefined, divisionFactor: number | undefined) => {
    return typeof value === 'number' ? (value / (divisionFactor || 1)).toString().replace('.', ',') : ''
}

const MetricInput = forwardRef<MetricInputRef, MetricInputProps>((props, ref) => {
    const {id, style, label, info, placeholder, divisionFactor, precision, min, max, prepend, append, readOnly, disabled, isInvalid} = props
    const factor = divisionFactor === undefined ? 1 : divisionFactor

    const [value, setValue] = useState(typeof props.value === 'number' ? props.value : 0)
    const [displayValue, setDisplayValue] = useState(toDisplayValue(props.value, divisionFactor))

    const formControl = useRef<InputRef | null>(null)

    // True when the text was edited since the last commit, so focusing and leaving the field doesn't call onChange
    // (it would store 0 for an undefined value)
    const changedRef = useRef(false)

    const regex = useMemo(() => {
        const maxDecimals = (divisionFactor || 1).toString().length - 1 + (precision || 0)

        return new RegExp(
            `^(?:$|[0-9]{0,20}|[0-9]{0,20}[,.][0-9]{0,${maxDecimals}})$`
        )
    }, [divisionFactor, precision])

    // Reset the internal value whenever the value, divisionFactor or precision props change
    const isFirstRender = useRef(true)
    useLayoutEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false
            return
        }

        setValue(typeof props.value === 'number' ? props.value : 0)
        setDisplayValue(toDisplayValue(props.value, divisionFactor))
        changedRef.current = false
    }, [props.value, divisionFactor, precision])

    useImperativeHandle(ref, () => ({
        focus: () => formControl.current?.focus(),
        blur: () => formControl.current?.blur(),
        setInvalid: (isInvalid = true) => formControl.current?.setInvalid(isInvalid)
    }), [])

    const commit = () => {
        if (!changedRef.current || disabled || readOnly) {
            return
        }

        changedRef.current = false

        props.onChange?.({target: {value}})
    }

    // Commit on Enter too: a parent that confirms on Enter (e.g. Popup) would otherwise read the value from before typing.
    // This runs in React's keydown handler, so the parent's state update is flushed before Popup's document listener runs.
    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            commit()
        }
    }

    const onChange = (event: InputChangeEvent) => {
        // The regex allows (digits of divisionFactor - 1) + precision decimals, so for the power-of-ten factors used the
        // stored value has at most `precision` decimals. Round to that to drop float noise ('0,29' * 100 = 28.999999999999996).
        const precisionFactor = 10 ** (precision || 0)
        const newValue = (Math.round(parseFloat(event.target.value.replace(',', '.')) * factor * precisionFactor) / precisionFactor) || 0
        const newDisplayValue = event.target.value.replace('.', ',')

        if (typeof min === 'number' && newValue < min) {
            return
        }

        if (typeof max === 'number' && newValue > max) {
            return
        }

        setValue(newValue)
        setDisplayValue(newDisplayValue)
        changedRef.current = true
    }

    return (
        <Input
            id={id}
            placeholder={typeof placeholder === 'string' ? placeholder : (factor && placeholder) ? (placeholder / factor).toString().replace('.', ',') : placeholder || ''}
            style={style}
            regex={regex}
            label={label}
            info={info}
            value={displayValue}
            onBlur={commit}
            onKeyDown={onKeyDown}
            onChange={onChange}
            prepend={prepend}
            append={append}
            readOnly={readOnly}
            disabled={disabled}
            isInvalid={isInvalid}
            ref={formControl}
        />
    )
})

MetricInput.displayName = 'MetricInput'

export default MetricInput
